import { useState, useMemo, useEffect, useRef } from "react";
// import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';
import { useParams, useNavigate } from "react-router-dom";
import Deadline from "./Deadline";
import CodingRow from "./CodingRow";
import CodingBudgetLine from "./CodingBudgetLine";
import { useSession } from "../components/SessionProvider";
import { useApi } from "../hooks/useApi";
import { useDownloadWithToken } from "../hooks/downloadApi";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import MultiSelectFilter from "./MultiSelectFilter";
import ConfirmDialog from "./ui/ConfirmDialog";
import AsyncSelect from "react-select/async";
import { Link } from "react-router-dom";
import AccountingAmount from "../components/ui/AccountingAmount";
import InfoTooltip from "../components/ui/InfoTooltip";
const CodingSheet = () => {
    const { apiCall } = useApi();
    const { downloadWithToken } = useDownloadWithToken();
    const isDevelopment = import.meta.env.VITE_APP_ENV === 'development';

    const [isdialogopen, setIsdialogopen] = useState(false);
    const initialized = useRef(false);
    const [costCodes, setCostCodes] = useState([]);
    const [selectedCodes, setselectedCodes] = useState({});
    const [summaryData, setSummaryData] = useState('');
    const [uncodedCount, setUncodedCount] = useState(null);
    const [codingSheet, setCodingSheet] = useState(null);
    const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

    const [gateNumber, setGateNumber] = useState('');

    const detailedInvoicesUrl = import.meta.env.VITE_DETAILED_INVOICES_URL;
    const [summaryDone, setSummaryDone] = useState(null);
    const { siteId, invoiceDate } = useParams();  // Get the site ID from the URL

    const [sorting, setSorting] = useState([]);
    const [focusTargetId, setFocusTargetId] = useState(null);
    const [lastEnteredCode, setLastEnteredCode] = useState(null);
    const [lastEnteredBudgetCode, setLastEnteredBudgetCode] = useState(null);
    const [hideZeroDollarItems, setHideZeroDollarItems] = useState(false);
    const [showUncodedItemsOnly, setShowUncodedItemsOnly] = useState(false);
    const [globalCode, setGlobalCode] = useState(null);
    const [globalBudgetLine, setGlobalBudgetLine] = useState(null);

    const [includeFilledItems, setIncludeFilledItems] = useState(false);
    const [openCodeDialog, setOpenCodeDialog] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const { userLogin } = useSession();
    const [unlockCodingSheet, setUnlockCodingSheet] = useState(false);
    const [totalUnCodedCount, setTotalUnCodedCount] = useState(0);
    const [printLoading, setPrintLoading] = useState(false);
    const [budgetLines, setBudgetLines] = useState(null);
    const [lastRowId, setLastRowId] = useState(null);
    const [hasBudgetLines, setHasBudgetLines] = useState(false);
    const [useBudgetLines, setUseBudgetLines] = useState(true);
    const [hasTax, setHasTax] = useState(false);
    const [gate3Error, setGate3Error] = useState('');
    // Regex matches: exactly one uppercase letter, a hyphen, one digit, a hyphen, and one digit
    const exactPattern = /^[A-Z0-9]+-[0-9]+(?:-[0-9]+)?$/;

    const navigate = useNavigate();
    // Track selected filters
    const [filters, setFilters] = useState({
        product_description: [],
        product_number: [],
        invoice_number: [],
        /* qty: [],
        product_type: [],
        billed_thru: [],
        date_out: [],
        rate_price: [],
        total: [], */
    });
    const format_number = (total) => {
        const num = Number(total);
        if (num === 0) return '$ -';

        const formatted = Math.abs(num).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        return num < 0 ? `$ (${formatted})` : `$ ${formatted}`;
    };
    const safeFormat = (dateValue) => {
        if (!dateValue || dateValue === '0000-00-00') {
            return '';
        }

        try {
            return format(parseISO(dateValue), 'dd-MMM-yyyy');
        } catch {
            return '';
        }
    };
    const getCodeString = (matched) => {
        return `${matched.code}-${matched.costType ? `${matched.costType}` : ''}-${matched.phase ? `${matched.phase}` : ''}-${matched.location ? `${matched.location}` : ''} - ${matched.description}`;
    }
    const loadCostCodesOptions = async (inputValue, callback) => {
        if (!inputValue.trim()) return callback([]);
        try {
            const response = await apiCall(`search_cost_codes`, { params: { site_id: siteId, search_term: inputValue } });
            const data = await response.json();
            const options = data.map(code => ({
                value: code.dbid,
                label: getCodeString(code)
            }));
            callback(options);
        } catch (error) {
            console.error("Error loading codes", error);
            callback([]);
        }
    };

    const loadBudgetLinesOptionsPerRow = (costCodeId) => {
        if (!costCodeId || !budgetLines) return [];
        return budgetLines
            .filter(budgetline => budgetline.cost_code_id === costCodeId)
            .map(budgetline => ({
                value: budgetline.b_dbid,
                label: budgetline.budget_line
            }));
    };
    const searchBudgetLinesOptionsPerRow = (costCodeId) => {
        return async (inputValue) => {
            if (!costCodeId || !inputValue.trim() || !budgetLines) return [];

            const options = budgetLines
                .filter(budgetline =>
                    budgetline.cost_code_id === costCodeId
                )
                .filter(budgetline => budgetline.budget_line
                    .includes(inputValue)
                )
                .map(budgetline => ({
                    value: budgetline.b_dbid,
                    label: budgetline.budget_line
                }));
            return options;
        };
    };
    // Fetch coding sheet
    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            apiCall(`fetch_summary_data`, { params: { site_id: siteId, invoice_date: invoiceDate } })
                .then((res) => res.json())
                .then((data) => {
                    setSummaryData(data);
                    setSummaryDone(data.summary_invoice || userLogin.read_only ? true : false)
                    //if it has summary invoice, then it was all coded.
                    //disable submit and other changes.
                    //@todo, if the submission date has passed
                    setGateNumber(data.gate_3_po);
                })
                .then(() => {
                    apiCall(`fetch_coding_sheet`, { params: { site_id: siteId, invoice_date: invoiceDate } })
                        .then((res) => res.json())
                        .then((data) => {
                            const cleanedData = data.map(item => ({
                                ...item,
                                billed_thru: safeFormat(item.billed_thru),
                                date_out: safeFormat(item.date_out),
                                total: Number(item.total) + Number(item.tax2_amount),
                            }));
                            setCodingSheet(cleanedData);
                        }).then(() => {
                            apiCall(`fetch_costcodes`, { params: { site_id: siteId } })
                                .then((res) => res.json())
                                .then((data) => {
                                    setCostCodes(data);

                                })
                                .catch((err) => console.error("Error loading cost codes:", err))
                        }).then(() => {
                            apiCall(`fetch_budgetlines`, { params: { site_id: siteId } })
                                .then((res) => res.json())
                                .then((data) => {
                                    setBudgetLines(data);

                                })
                                .catch((err) => console.error("Error loading budget lines:", err))
                        })

                        .catch((err) => console.error("Error loading coding sheet:", err));
                })
                .catch((err) => console.error("Error loading gateNumber:", err));


            apiCall(`getCodingSheetUncodedCount`, { params: { invoice_date: invoiceDate } })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error("Failed to fetch sites");
                    }
                    return response.json();
                }).then((data) => {
                    if (data.count) {
                        setTotalUnCodedCount(data.count);
                    }

                });
        }
    });


    const checkIfUncoded = (item) => {
        return !(item.cost_code_id && item.cost_code_id !== '');

        //if budget line is mandatory
        /* const missingCode = !parseInt(item.cost_code_id);
        const missingBudgetLine = parseInt(item.cost_code_id) &&
            hasCostCodeBudgetLines(item.cost_code_id) &&
            !item.budget_line_id;

        return (
            Number(item.total) !== 0 &&
            (missingCode || missingBudgetLine)
        ); */
    };
    useEffect(() => {
        if (codingSheet && codingSheet.length > 0) {
            const uncodedCountTemp = codingSheet
                ? codingSheet.filter(item => {
                    return checkIfUncoded(item);
                }).length
                : 0;
            //get the budget lines coded count
            setUncodedCount(uncodedCountTemp);
        }
    }, [codingSheet, budgetLines]); // NO selectedCodes dependency!


    useEffect(() => {
        if (codingSheet) {
            if (summaryDone) {
                //check if it has any budget lines, after summary is done, if not,
                //don't show the column

                const hasBudgetLines = codingSheet.some(row => row.budget_line_id !== '' && row.budget_line_id != null);
                setHasBudgetLines(hasBudgetLines);
            }
            else if (budgetLines && budgetLines.length > 0) {
                setHasBudgetLines(true);
            }

            //check if it has tax
            const has_tax = codingSheet.some(item => Number(item.tax2_amount) !== 0);
            setHasTax(has_tax);
        }

    }, [codingSheet, summaryDone, budgetLines]);
    useEffect(() => {
        if (!siteId || !invoiceDate || !costCodes || costCodes.length === 0 || summaryDone) return;

        const fetchLatestCostCodes = () => {
            apiCall(`latest_coding_sheet_codes`, { params: { site_id: siteId, invoice_date: invoiceDate } })
                .then(res => res.json())
                .then(data => {
                    if (!data?.length) return;

                    const updatesByInvoiceItem = new Map(
                        data.map(d => [d.invoice_item_id, d])
                    );

                    setCodingSheet(prev => {
                        let changed = false;

                        const next = prev.map(row => {
                            const update = updatesByInvoiceItem.get(row.invoice_item_id);

                            if (!update) return row;

                            if (
                                row.cost_code_id === update.cost_code_id &&
                                row.budget_line_id === update.budget_line_id
                            ) {
                                return row;
                            }

                            changed = true;

                            return {
                                ...row,
                                cost_code_id: update.cost_code_id,
                                budget_line_id: update.budget_line_id
                            };
                        });
                        return changed ? next : prev;
                    });
                })
                .catch(err => console.error("Error polling for updates:", err));
        };

        fetchLatestCostCodes();
        const intervalId = setInterval(fetchLatestCostCodes, 2000);

        return () => clearInterval(intervalId);
    }, [siteId, invoiceDate, costCodes, summaryDone]);

    const checkGate3Code = () => {
        return gateNumber && gateNumber.length > 0 && exactPattern.test(gateNumber);
    }
    const handleGateNumberChange = (e) => {

        let inputValue = e.target.value.toUpperCase();

        // Auto-insert hyphens as they type to prevent friction

        setGateNumber(inputValue);

        // Validate the current string
        if (inputValue.length === 0) {
            setGate3Error(''); // Clear error if empty
        } else if (!exactPattern.test(inputValue)) {
            setGate3Error('Format must be Alphanumeric-Numbers[-Numbers] (e.g., T-125-22)');
        } else {
            setGate3Error(''); // Clear error if fully valid
        }

    };
    // Submit Coding Sheet
    const submitSheetsFunction = () => {

        if (inProgress) return;

        setInProgress(true);

        let success = false;

        if (!checkGate3Code()) {
            setGate3Error('Invalid format. Use Alphanumeric-Numbers[-Numbers]. The last section is optional.');
        }
        else {
            // Send partial update to backend
            apiCall(`update_gate_number`, {
                method: 'POST',
                body: { site_id: siteId, invoice_date: invoiceDate, gate_number: gateNumber }
            }).then(res => res.json())
                .then(data => {
                    if (!data.success) {
                        console.error('Error updating:', data.error);
                        setGate3Error('Some error occured updating gate number');
                        setInProgress(false);
                        setIsdialogopen(false);
                    }
                    else {
                        apiCall(`generateSummaryInvoice`, {
                            method: "POST",
                            params: { site_id: siteId, invoice_date: invoiceDate, u: userLogin.user_email, remove_budget_lines: !useBudgetLines },

                        })
                            .then((res) => res.json())
                            .then((data) => {
                                if (data.success) {
                                    //alert("Coding sheet submitted successfully!");
                                    setIsSubmitEnabled(false);
                                    setSummaryDone(true);
                                    success = true;
                                } else {

                                    if (data.error) {
                                        alert(data.error);
                                    }
                                    else {
                                        alert("Error submitting coding sheet.");
                                    }

                                }
                            }).finally(() => {
                                setInProgress(false);
                                if (success) {
                                    navigate(`/projects/${siteId}/`);

                                }
                            })
                            .catch((err) => {
                                alert("Error submitting coding sheet.");
                                console.error("Error submitting coding sheet:", err)
                            });
                    }
                })
                .catch(err => console.error('Error updating:', err));
        }


    };

    useEffect(() => {
        if (codingSheet && codingSheet.length > 0 && !summaryDone) {
            setIsSubmitEnabled(checkGate3Code() && uncodedCount === 0);
        } else {
            setIsSubmitEnabled(false);
        }
    }, [gateNumber, codingSheet, uncodedCount, summaryDone]);

    if (codingSheet && codingSheet.length == 0) {
        return <div className="text-center text-lg font-semibold">No coding sheet available for this month.</div>;
    }
    const applyBudgetLineToItem = (invoiceItemId, budgetLine = null) => {
        // optimistic UI update
        setCodingSheet(prev =>
            prev.map(row =>
                row.invoice_item_id === invoiceItemId
                    ? { ...row, budget_line_id: budgetLine }
                    : row
            )
        );

        // server update
        apiCall(`update_budget_line`, {
            method: "POST",
            body: {
                invoice_item_id: invoiceItemId,
                budget_line_id: budgetLine
            }
        }).catch(() => {
            // optional rollback later
        });

        setLastEnteredBudgetCode(budgetLine);
        setLastRowId(invoiceItemId);

    };
    const applyCostCodeToItem = (invoiceItemId, costCodeId, hasPasteAction = false) => {
        let newBudgetId = null;
        if (hasPasteAction === true && lastEnteredBudgetCode) {
            newBudgetId = lastEnteredBudgetCode;
        }

        /* if (parseInt(costCodeId) && hasCostCodeBudgetLines(costCodeId)) {
            const budgetLinesRow = loadBudgetLinesOptionsPerRow(costCodeId);
            //and if only 1, then set that one
            if (budgetLinesRow.length == 1) {
                newBudgetId = budgetLinesRow[0].value;
            }

        } */



        // optimistic UI update
        setCodingSheet(prev =>
            prev.map(row =>
                row.invoice_item_id === invoiceItemId
                    ? { ...row, cost_code_id: costCodeId, budget_line_id: newBudgetId }
                    : row
            )
        );

        // server update
        apiCall(`update_cost_code_and_budget_line`, {
            method: "POST",
            body: {
                invoice_item_id: invoiceItemId,
                cost_code_id: costCodeId,
                budget_line_id: newBudgetId
            }
        }).catch(() => {
            // optional rollback later
        });

        setLastEnteredCode(costCodeId);
        setLastEnteredBudgetCode(newBudgetId);
        setLastRowId(invoiceItemId);

    };

    const applyGlobalCode = () => {
        if (!globalCode) {
            console.log('no global code');
            return;
        }
        const list_to_update = [];
        filteredData.forEach(item => {
            //only the items with total above 0
            if (includeFilledItems) {
                //apply to all
                list_to_update.push(item.invoice_item_id);
            }
            else if (!item.cost_code_id || item.cost_code_id == "" || item.cost_code_id == 0) {
                //only if empty
                list_to_update.push(item.invoice_item_id);
            }


        });

        if (list_to_update.length) {
            const budgetLineValue = globalBudgetLine ?? null;
            setCodingSheet(prev =>
                prev.map(row => {
                    if (list_to_update.includes(row.invoice_item_id)) {
                        return {
                            ...row,
                            cost_code_id: globalCode,
                            budget_line_id: budgetLineValue
                        };
                    }

                    return row;
                })
            );
            // server update
            apiCall(`update_cost_code_bulk`, {
                method: "POST",
                body: {
                    invoice_item_id: list_to_update,
                    cost_code_id: globalCode,
                    budget_line_id: budgetLineValue

                }
            }).catch(() => {
                // optional rollback later
            });

            setLastEnteredCode(globalCode);
            setFocusTargetId(null);
            if (includeFilledItems) {
                setOpenCodeDialog(false);
            }

            //reset the values once applied.
            globalCodeChange(null);
            setIncludeFilledItems(false);
        }

    };

    const hasCostCodeBudgetLines = (cost_code_id) => {
        const costCodeBudgetLines = loadBudgetLinesOptionsPerRow(cost_code_id);
        if (costCodeBudgetLines && costCodeBudgetLines.length > 0) {
            return true;
        }

        return false;
    };

    // SVG Icons
    const ExcelSVG = () => (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: '8px' }}
        >
            <rect
                x="4"
                y="2"
                width="16"
                height="20"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.5"
            />
            <path
                d="M8 7L16 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
            <path
                d="M8 11L16 11"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
            <path
                d="M8 15L13 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        </svg>
    );

    const handleExport = async () => {
        setPrintLoading(true);
        try {
            await downloadWithToken('generateCurrentCodingSheet', {
                invoice_date: invoiceDate,
                site_id: siteId
            }, `export_${new Date().getTime()}.xlsx`);
        } catch (error) {
            console.error('Export failed:', error);
            // Optionally show error message to user
        } finally {
            setPrintLoading(false);
        }
    };

    const globalCodeChange = async (option) => {
        if (!option) {
            setGlobalCode(null);
            setGlobalBudgetLine(null);
            return;
        }

        const code = option.value;

        setGlobalCode(code);

        if (hasBudgetLines) {
            setGlobalBudgetLine(null);
            /* const budgetLinesRow = loadBudgetLinesOptionsPerRow(code);

            if (budgetLinesRow.length === 1) {
                setGlobalBudgetLine(budgetLinesRow[0].value);
            } else {
                setGlobalBudgetLine(null);
            } */
        }

    };
    const formatQty = (qty) => {
        return Number(qty).toFixed(2).replace(/\.?0+$/, '');
    }

    const columns = useMemo(() => [

        {
            accessorKey: 'product_number',
            header: 'Product #',
            filterFn: 'includesString',
            meta: { className: "min-w-[120px]" }
        },
        {
            accessorKey: 'product_description',
            header: 'Description',
            filterFn: 'includesString',
            meta: { className: "w-auto whitespace-nowrap" }

        },
        {
            accessorKey: 'invoice_number',
            header: 'Invoice No.',
            filterFn: 'includesString',
            meta: { className: "min-w-[110px]" },


            cell: ({ row }) => {
                const invoice_file = row.original.invoice_file;
                return invoice_file ? <a href={detailedInvoicesUrl + row.original.invoice_file} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline mr-2">
                    {row.original.invoice_number}
                </a> : row.original.invoice_number;
            }

        },
        {
            accessorKey: 'qty',
            header: 'Qty',
            cell: ({ row }) => {
                return formatQty(row.original.qty)
                //return row.original.date_out != '0000-00-00' ? format(parseISO(row.original.date_out), 'dd-MMM-yyyy') : '';
            }
        },
        {
            accessorKey: 'date_out',
            header: 'Billed From',
            filterFn: 'includesString',
            meta: { className: "min-w-[115px]" },

            cell: ({ row }) => {
                return row.original.date_out
                //return row.original.date_out != '0000-00-00' ? format(parseISO(row.original.date_out), 'dd-MMM-yyyy') : '';
            }
        },
        {
            accessorKey: 'billed_thru',
            header: 'Billed To',
            filterFn: 'includesString',
            meta: { className: "min-w-[115px]" },

            cell: ({ row }) => {
                //if this is '0000-00-00', return empty string
                return row.original.billed_thru
            }
        },
        {
            accessorKey: 'rate_price',
            header: 'Rate/Price	',
            filterFn: 'includesString',
            cell: ({ row }) => {

                return <AccountingAmount total={row.original.rate_price} />

            }
        },
        ...(hasTax ? [{
            accessorKey: 'tax2_amount',
            header: 'PST',
            meta: { className: "w-auto whitespace-nowrap" },
            cell: ({ row }) => {

                return <AccountingAmount total={row.original.tax2_amount} />
            }
        }] : []),
        {
            accessorKey: 'total',
            header: () => (
                <>
                    Total {hasTax ? <small className="font-semibold">(Incl. Tax)</small> : ''}
                </>
            ),
            filterFn: 'includesString',
            cell: ({ row }) => {
                return <AccountingAmount total={row.original.total} />
            }
        },
        {
            accessorKey: 'costcode',
            header: 'Cost Code',
            meta: { className: "w-auto whitespace-nowrap" },
            cell: ({ row }) => {
                return (
                    <CodingRow
                        row={row.original}
                        serverCostCodeId={row.original.cost_code_id}
                        costCodes={costCodes}
                        loadCostCodesOptions={loadCostCodesOptions}
                        summaryDone={summaryDone}
                        setCodingSheet={setCodingSheet}
                        isNextFocus={focusTargetId === row.original.invoice_item_id}
                        lastEnteredCode={lastEnteredCode}
                        setLastEnteredCode={setLastEnteredCode}
                        applyCostCodeToItem={applyCostCodeToItem}
                        getCodeString={getCodeString}
                    />
                );
            }
        },
        ...(hasBudgetLines && useBudgetLines ? [{
            accessorKey: 'budget_line',
            header: 'Budget Line',
            meta: { className: "w-auto whitespace-nowrap" },
            cell: ({ row }) => {
                return (
                    <CodingBudgetLine
                        row={row.original}
                        serverBudgetLineId={row.original.budget_line_id}
                        applyBudgetLineToItem={applyBudgetLineToItem}
                        summaryDone={summaryDone}
                        budgetLines={budgetLines}
                        loadBudgetLinesOptionsPerRow={loadBudgetLinesOptionsPerRow}
                        searchBudgetLinesOptionsPerRow={searchBudgetLinesOptionsPerRow}
                    />
                );
            }
        }] : []),
        // Add more as needed
    ], [hasBudgetLines, useBudgetLines, budgetLines, costCodes, summaryDone, focusTargetId, showUncodedItemsOnly, lastEnteredCode, lastEnteredBudgetCode]);




    // Filter data based on selected dropdowns
    const filteredData = useMemo(() => {
        if (!Array.isArray(codingSheet)) return [];
        return codingSheet.filter((row) =>
            Object.keys(filters).every((key) => {
                const filterValues = filters[key];

                // ✅ If no filters applied for this key, include all
                if (!filterValues || filterValues.length === 0) return true;

                // ✅ Numeric comparison for qty
                if (key === "qty") {
                    return filterValues.some(
                        (val) => Number(row[key]) === Number(val)
                    );
                }

                // ✅ String comparison for others
                return filterValues.includes(String(row[key]));
            })
        ).filter((row) => {
            return hideZeroDollarItems ? (Number(row.total) === 0 ? false : true) : true
        }).filter((row) => {
            return showUncodedItemsOnly ? (checkIfUncoded(row) ? true : false) : true
        });

    }, [codingSheet, hideZeroDollarItems, showUncodedItemsOnly, filters]);


    const getFilteredValues = (key) => {
        if (!Array.isArray(codingSheet)) return { allValues: [], visibleValues: new Set() };

        // 1️⃣ Get all possible values (full dataset)
        const allValues = [...new Set(
            codingSheet
                .map(item => item[key])
                .filter(v => v !== null && v !== undefined && v !== "")
        )].sort((a, b) => key === "qty" ? Number(a) - Number(b) : String(a).localeCompare(String(b)));

        // 2️⃣ Filter dataset ignoring the current column’s own filter
        const filteredExceptThis = codingSheet.filter((row) =>
            Object.keys(filters).every((fKey) => {
                if (fKey === key) return true; // skip current column
                const values = filters[fKey];
                if (!values || values.length === 0) return true;
                if (fKey === "qty") return values.some(v => Number(row[fKey]) === Number(v));
                return values.includes(String(row[fKey]));
            })
        );

        // 3️⃣ Compute visible values within that reduced set
        const visibleValues = new Set(filteredExceptThis.map((r) => String(r[key])));

        return { allValues, visibleValues };
    };

    const handleFilterChange = (key, values) => {
        setFilters((prev) => ({
            ...prev,
            [key]: values,
        }));
    };



    const sub_total = filteredData.reduce((sum, item) => sum + (Number(item.total) || 0), 0);

    const cost_codes_totals_values = useMemo(() => {
        if (!Array.isArray(costCodes) || !Array.isArray(filteredData) || filteredData.length < 1) return [];
        const total_cost_codes = {}; // object, not array
        const use_new_style = filteredData[0].cost_code_string;

        filteredData.forEach(row => {
            const costCodeId = use_new_style ? row.cost_code_string : row.cost_code_id;
            const total = Number(row.total) || 0;
            if (costCodeId != null) {
                if (!total_cost_codes[costCodeId]) {
                    if (use_new_style) {
                        total_cost_codes[costCodeId] = { code: costCodeId, description: row.cost_code_description, total: total };
                    }
                    else {
                        const matched = costCodes.find(c => c.dbid == costCodeId);
                        if (matched) {
                            total_cost_codes[costCodeId] = { code: matched.code, description: matched.description, total: total };

                        }
                    }

                }
                else {
                    total_cost_codes[costCodeId].total += total;

                }

            }

        });


        return total_cost_codes;

    }, [totalUnCodedCount, costCodes, lastEnteredCode, filteredData]);



    const table = useReactTable({
        data: filteredData ? filteredData : [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });
    //set the focus row
    useEffect(() => {
        if (!table || !lastRowId) return;

        const rows = table.getRowModel().rows;
        // 1. Find the current row and its index in the flat table structure
        const currentIndex = rows.findIndex(r => r.original.invoice_item_id === lastRowId);
        //if this is not in the visible rows, return
        if (currentIndex === -1) return;

        const currentRow = rows[currentIndex].original;

        // 2. If the user hasn't finished coding the CURRENT row, force focus to stay here
        if (checkIfUncoded(currentRow)) {
            setFocusTargetId(currentRow.invoice_item_id);
            return;
        }

        // 3. Current row is valid! Scan forward from the next row to find the next uncoded target
        let nextFocusId = null;
        for (let i = currentIndex + 1; i < rows.length; i++) {
            const scanRow = rows[i].original;
            if (checkIfUncoded(scanRow)) {
                nextFocusId = scanRow.invoice_item_id;
                break; // Stop at the very first empty row we find
            }
        }

        // 4. Update the focus state (will be null if all subsequent rows are fully coded)
        setFocusTargetId(nextFocusId);

    }, [lastRowId, table, lastEnteredBudgetCode, lastEnteredCode]); // Added missing dependencies to prevent infinite re-renders


    const codedBudgetLines = codingSheet && codingSheet.length > 0 ? codingSheet.filter(item => item.budget_line_id && item.budget_line_id !== '').length : 0;

    /*     const taxes_total = filteredData.reduce((sum, item) => sum + (Number(item.tax) || 0), 0);
        const grand_total = Number(sub_total) + Number(taxes_total); */
    return (
        <>
            {/* Coding Sheet Table */}
            {filteredData && budgetLines && selectedCodes && summaryDone !== null && (
                <>
                    <div className="mt-4 w-full">
                        <h3 className="text-2xl font-bold mb-3" style={{ color: '#000080' }}>Coding Sheet</h3>
                        {!summaryDone && (
                            <Deadline />
                        )}
                        <>
                            <div className="mb-5 relative">

                                <Link to={`/projects/${siteId}/`} className={`absolute top-[${unlockCodingSheet || !summaryDone ? '0px' : '-50px'}] right-0 btn btn-primary self-end ` + (summaryDone ? 'top-[-50px]' : 'top-[70px]')}>
                                    Back
                                </Link>
                                <button
                                    onClick={handleExport}
                                    disabled={printLoading}
                                    className="absolute right-0 top-3"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        padding: '10px 20px',
                                        backgroundColor: printLoading ? '#ccc' : '#4CAF50',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: printLoading ? 'not-allowed' : 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    <ExcelSVG />
                                    {printLoading ? 'Exporting...' : 'Export to Excel'}
                                </button>


                                <div className="text-gray-700">
                                    <input
                                        name="gate_number"
                                        type="text"
                                        maxLength={15}
                                        placeholder="Gate Three Sub-PO # e.g. (T-1-3)"
                                        value={gateNumber ?? ''}
                                        onChange={handleGateNumberChange}
                                        className="border p-2 rounded w-full font-medium"
                                        disabled={summaryDone || uncodedCount > 0}
                                        style={{ "width": "270px" }}
                                    />

                                    {gate3Error && (
                                        <div className="text-red-600 text-sm">{gate3Error}</div>
                                    )}
                                    {/*once all the coding sheets are coded, can not edit anymore */}
                                    {userLogin.isAdminManager && summaryDone && !unlockCodingSheet && totalUnCodedCount !== 0 && (
                                        <button
                                            onClick={() => { setSummaryDone(false); setUnlockCodingSheet(true) }}
                                            className="ml-3 bg-green-500 text-white px-4 py-2 rounded mt-4 cursor-pointer disabled:opacity-50 }"
                                        >
                                            Unlock Coding Sheet
                                        </button>



                                    )}


                                    {!summaryDone && (
                                        <>
                                            <ConfirmDialog
                                                open={isdialogopen}
                                                title="Submission Confirmation"
                                                message={"Are you sure you want to submit the coding sheet? <br> You will NOT be able to make changes after submission."
                                                    + (!useBudgetLines && codedBudgetLines ? "<div class='text-red-600'><br><br>You have added " + codedBudgetLines + " budget lines while you opted not to use them.<br>Existing budget lines will be removed.</div>" : '')
                                                }
                                                confirmText="Submit"
                                                cancelText="Cancel"
                                                onConfirm={submitSheetsFunction}
                                                inProgress={inProgress}
                                                onCancel={() => setIsdialogopen(false)}
                                            />
                                            <div className="pt-3">
                                                <button
                                                    onClick={() => setIsdialogopen(true)}
                                                    className="bg-green-500 text-white px-4 py-2 rounded mt-4 cursor-pointer disabled:opacity-50 }"
                                                    disabled={!isSubmitEnabled}
                                                >
                                                    Submit Coding Sheet
                                                </button>

                                                <div>
                                                    <div className="mb-[-74px] flex w-fit flex-col">
                                                        <p className="mt-2">Please enter all the cost codes and the Sub Purchase Order #.</p>
                                                        <div>
                                                            <div className="flex justify-between mb-5">
                                                                <p className="text-red-600">Please ensure only one person submits coding sheet!</p>

                                                            </div>


                                                        </div>
                                                    </div>
                                                    <div className="flex justify-between gap-10 items-end">
                                                        <div className="w-[33%]">
                                                            {/* <label className="flex w-fit items-center mb-4">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={hideZeroDollarItems}
                                                                    onChange={(e) => setHideZeroDollarItems(e.target.checked)}
                                                                    className="mr-2"
                                                                />
                                                                Hide Zero Dollar Items
                                                            </label> */}
                                                            <label className="flex w-fit items-center mb-4">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={showUncodedItemsOnly}
                                                                    onChange={(e) => setShowUncodedItemsOnly(e.target.checked)}
                                                                    className="mr-2"
                                                                />
                                                                Show Uncoded Items Only
                                                            </label>
                                                        </div>
                                                        <div className="z-55 relative mx-auto mb-6 flex-col items-center flex w-[33.33%]">
                                                            <p className="mb-3"><b>Code Multiple Line Items at Once</b></p>
                                                            <ConfirmDialog
                                                                open={openCodeDialog && includeFilledItems}
                                                                title="Submission Confirmation"
                                                                message={"Are you sure you want to apply this code to all visible rows?<br>This will overwrite the current cost codes that are showing."}
                                                                confirmText="Submit"
                                                                cancelText="Cancel"
                                                                onConfirm={applyGlobalCode}
                                                                onCancel={() => setOpenCodeDialog(false)}
                                                            />
                                                            <AsyncSelect
                                                                className="w-[100%]"
                                                                cacheOptions
                                                                isClearable
                                                                value={
                                                                    globalCode
                                                                        ? (() => {
                                                                            const found = costCodes.find(b => b.dbid == globalCode);
                                                                            return found ? { value: found.dbid, label: getCodeString(found) } : null;
                                                                        })()
                                                                        : null
                                                                }
                                                                loadOptions={loadCostCodesOptions}
                                                                onChange={globalCodeChange}
                                                                placeholder="Search Cost Codes..."
                                                                noOptionsMessage={({ inputValue }) =>
                                                                    inputValue ? "No results found" : "Type code # or name to search"
                                                                }
                                                            />
                                                            {hasBudgetLines && useBudgetLines && (
                                                                <AsyncSelect
                                                                    className="w-[50%] mt-3"
                                                                    value={
                                                                        globalBudgetLine
                                                                            ? (() => {
                                                                                const found = budgetLines.find(b => b.b_dbid == globalBudgetLine);
                                                                                return found ? { value: found.b_dbid, label: found.budget_line } : null;
                                                                            })()
                                                                            : null
                                                                    }
                                                                    cacheOptions
                                                                    loadOptions={() => loadBudgetLinesOptionsPerRow(globalCode)}
                                                                    defaultOptions={loadBudgetLinesOptionsPerRow(globalCode)}

                                                                    onChange={(option) =>
                                                                        option
                                                                            ? setGlobalBudgetLine(option.value)
                                                                            : setGlobalBudgetLine(null)
                                                                    }
                                                                    placeholder="Search Budget Lines..."
                                                                    noOptionsMessage={({ inputValue }) =>
                                                                        inputValue
                                                                            ? "No results found"
                                                                            : "Type a budget line to search"
                                                                    }
                                                                />
                                                            )}

                                                            <label className="flex w-fit items-center my-2 ">
                                                                <input
                                                                    className="mr-2"
                                                                    type="checkbox"
                                                                    checked={includeFilledItems}
                                                                    onChange={(e) => setIncludeFilledItems(e.target.checked)}
                                                                />
                                                                Replace Already Coded Fields
                                                            </label>
                                                            <button
                                                                onClick={() => includeFilledItems ? setOpenCodeDialog(true) : applyGlobalCode()}
                                                                className="bg-green-500 text-white px-4 py-2 rounded cursor-pointer disabled:opacity-50 w-[150px]}"
                                                                disabled={!globalCode}
                                                            >
                                                                Apply To All Visible Rows
                                                            </button>

                                                        </div>
                                                        <div className="w-[33%]">

                                                            <div className="text-gray-700 font-medium text-right mr-2  mb-4">
                                                                Uncoded Items: <span className="text-red-600">{uncodedCount}</span>
                                                            </div>
                                                            <div className="text-right mr-2  mb-4"><i>Tip: After entering a code, you can paste the same code on the next line by clicking ctrl+v</i></div>
                                                            {hasBudgetLines && !summaryDone && (
                                                                <>

                                                                    <label className="flex w-fit justify-self-end items-center gap-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={!useBudgetLines}
                                                                            onChange={(e) => setUseBudgetLines(!e.target.checked)}
                                                                        />
                                                                        Don't Use budget lines.
                                                                    </label>
                                                                    <div className="text-right mb-4">
                                                                        <i>
                                                                            Check this box if your project does not require budget lines for Oxford.<br />
                                                                            If budget lines are required for Oxford, they must be entered.
                                                                        </i>
                                                                    </div>
                                                                </>
                                                            )
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>



                                            </div>


                                        </>
                                    )}

                                </div>

                            </div>

                        </>

                        <div className="pb-40" >
                            <table className="min-w-full border-collapse border border-gray-300">
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id} className="bg-gray-200" >
                                            {headerGroup.headers.map(header => (
                                                <th className={
                                                    "border p-2 w-auto whitespace-nowrap "
                                                    + 'col-' + (header.column.id) + ' '
                                                    + (header.column.columnDef.meta?.className || "")
                                                }

                                                    key={header.id}
                                                    onClick={header.column.columnDef.accessorKey != 'costcode' ? header.column.getToggleSortingHandler() : null}
                                                    style={{ cursor: header.column.id != 'costcode' ? 'pointer' : 'normal' }}
                                                >
                                                    <div>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}

                                                        {header.column.columnDef.accessorKey !== 'costcode' &&
                                                            (header.column.getIsSorted() === 'asc'
                                                                ? ' 🔼'
                                                                : header.column.getIsSorted() === 'desc'
                                                                    ? ' 🔽'
                                                                    : ''
                                                            )
                                                        }
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>

                                    ))}
                                </thead>

                                <tbody>
                                    {!summaryDone && table.getHeaderGroups().map(headerGroup => (
                                        <tr key={`filter-container-${headerGroup.id}`} className="bg-gray-200 sticky top-[40px] z-2" >
                                            {headerGroup.headers.map(header => (
                                                <td className="border p-2 text-center"
                                                    key={`search-${header.id}`}
                                                >
                                                    {(header.column.columnDef.accessorKey === "product_number"
                                                        || header.column.columnDef.accessorKey === "product_description"
                                                        || header.column.columnDef.accessorKey === "invoice_number") && (
                                                            <>
                                                                {Object.keys(filters).includes(header.id) && (() => {
                                                                    const hasActiveFilter = filters?.[header.id]?.length > 0;

                                                                    return (
                                                                        <>
                                                                            {/* <div
                                                                                className={`mb-1 select-none
              ${hasActiveFilter
                                                                                        ? "cursor-pointer text-gray-600 hover:text-gray-900 hover:underline"
                                                                                        : "text-gray-400 cursor-not-allowed"}
            `}
                                                                                onClick={() => {
                                                                                    if (hasActiveFilter) handleFilterChange(header.id, []);
                                                                                }}
                                                                            >
                                                                                ⟲ Reset filter
                                                                            </div> */}

                                                                            <MultiSelectFilter
                                                                                placeholder="All"
                                                                                options={getFilteredValues(header.id)}
                                                                                selectedValues={filters[header.id]}
                                                                                onChange={(selected) =>
                                                                                    handleFilterChange(header.id, selected)
                                                                                }
                                                                            />
                                                                        </>
                                                                    );
                                                                })()}
                                                            </>
                                                        )}

                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {table.getRowModel().rows.map(row => (
                                        <tr data-db-id={row.original.id} key={row.id} className="border" style={!summaryDone && !checkIfUncoded(row.original) ? { backgroundColor: 'lightblue' } : {}}>
                                            {row.getVisibleCells().map(cell => (
                                                <td key={cell.id} className={`border p-2 ${cell.column.id === 'product_description' ? 'uppercase' : ''} ${cell.column.id === '' ? '' : ''}   w-auto whitespace-nowrap`} >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {/* {(taxes_total > 0) && (
                                        <tr><td className="border p-2 font-bold" colSpan={7}>TAXES TOTAL</td><td className="border p-2 font-bold">{format_number(taxes_total) || '$0'}</td><td></td></tr>

                                    )} */}
                                    {cost_codes_totals_values && Object.keys(cost_codes_totals_values).length > 0 && (
                                        <>
                                            <tr><td className="border h-[40px]" colSpan={9}></td></tr>

                                            <tr style={{ color: '#000080' }}><td className="border p-2 font-bold" colSpan={hasTax ? '8' : '7'}>SUMMARY</td><td className="border p-2 font-bold"></td><td></td></tr>
                                            {Object.entries(cost_codes_totals_values).map(([code, costcodeobj]) => (
                                                <tr key={code} style={{ color: '#000080' }}>
                                                    <td className="border p-2 font-bold" colSpan={2}>{costcodeobj.code}</td>
                                                    <td className="border p-2 font-bold" colSpan={hasTax ? '6' : '5'}>{costcodeobj.description}</td>
                                                    <td className="border p-2 font-bold"><AccountingAmount total={costcodeobj.total} /></td>
                                                    <td></td>
                                                </tr>

                                            ))}
                                        </>


                                    )}
                                    <tr style={{ color: '#000080' }}><td className="border p-2 font-bold" colSpan={hasTax ? '8' : '7'}>TOTAL</td><td className="border p-2 font-bold"><AccountingAmount total={sub_total} /></td><td></td></tr>
                                </tbody>
                            </table>
                        </div>

                    </div>
                </>
            )}

        </>
    );
};

export default CodingSheet;